/**
 * Cola offline genérica (IndexedDB) para captura en campo sin internet.
 *
 * Estrategia de fotos serializable: el payload lleva un "placeholder"
 * (`__OFFLINE_PHOTO__<id>__`) donde irá la URL. Los blobs se guardan aparte en
 * IndexedDB. Al sincronizar, se suben los blobs a /upload/photo y se reemplaza
 * el placeholder por la URL real dentro del JSON antes de hacer el POST.
 *
 * Lo usan: Inspecciones de Planta, Inspecciones NOM y Actos/Condiciones Inseguras.
 */
import { openDB } from "idb";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DB_NAME = "hse-offline";
const DB_VERSION = 1;

let _dbp = null;
function db() {
  if (!_dbp) {
    _dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains("pending")) d.createObjectStore("pending", { keyPath: "id" });
        if (!d.objectStoreNames.contains("cache")) d.createObjectStore("cache", { keyPath: "key" });
      },
    });
  }
  return _dbp;
}

function uid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const PH = (id) => `__OFFLINE_PHOTO__${id}__`;
export const photoPlaceholder = PH;

// ===== Caché de catálogos (categorías, checklists, áreas) =====
export async function cachePut(key, value) {
  try { await (await db()).put("cache", { key, value, at: Date.now() }); } catch { /* noop */ }
}
export async function cacheGet(key) {
  try { const r = await (await db()).get("cache", key); return r ? r.value : null; } catch { return null; }
}

/** GET con respaldo en caché: si la red falla, devuelve la última copia cacheada. */
export async function getWithCache(path, cacheKey) {
  const url = path.startsWith("http") ? path : `${API}${path}`;
  try {
    const res = await axios.get(url);
    await cachePut(cacheKey, res.data);
    return res.data;
  } catch (e) {
    if (!e?.response) {
      const cached = await cacheGet(cacheKey);
      if (cached != null) return cached;
    }
    throw e;
  }
}

// ===== Cola de pendientes =====
export async function queueAdd(rec) {
  const id = rec.id || uid();
  const full = { ...rec, id, created_at: new Date().toISOString(), attempts: 0, error: null };
  await (await db()).put("pending", full);
  notify();
  return id;
}
export async function queueAll() { return (await db()).getAll("pending"); }
export async function queueDelete(id) { await (await db()).delete("pending", id); notify(); }
export async function queueCount() { try { return await (await db()).count("pending"); } catch { return 0; } }

function isNetworkError(e) { return !e?.response; }

const MAX_ATTEMPTS = 5;

// Sube los blobs y reemplaza los placeholders por URLs reales en el payload.
async function uploadAndResolve(payload, photos) {
  let json = JSON.stringify(payload);
  for (const p of (photos || [])) {
    const file = p.blob instanceof File
      ? p.blob
      : new File([p.blob], p.filename || "foto.jpg", { type: (p.blob && p.blob.type) || "image/jpeg" });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("related_to", p.related_to || "offline");
    const res = await axios.post(`${API}/upload/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    json = json.split(PH(p.placeholderId)).join(res.data.file_url);
  }
  return JSON.parse(json);
}

/**
 * Envía ahora si hay conexión; si no, encola para sincronizar después.
 * photos: [{ placeholderId, blob, related_to, filename }]
 * Lanza el error tal cual si es un error de validación/servidor (no de red).
 */
export async function submitOrQueue({ type, label, endpoint, method = "post", payload, photos }) {
  if (navigator.onLine) {
    try {
      const resolved = await uploadAndResolve(payload, photos);
      const res = await axios({ method, url: `${API}${endpoint}`, data: resolved });
      return { status: "online", data: res.data };
    } catch (e) {
      if (!isNetworkError(e)) throw e; // error real (4xx/5xx) -> propagar
      // fallo de red a mitad del envío -> encolar
    }
  }
  const id = await queueAdd({ type, label, endpoint, method, payload, photos });
  return { status: "queued", id };
}

// ===== Sincronización de todos los pendientes =====
let _syncing = false;
export async function syncAll() {
  if (_syncing || !navigator.onLine) return { synced: 0, failed: 0 };
  _syncing = true;
  let synced = 0, failed = 0;
  try {
    const all = await queueAll();
    for (const rec of all) {
      // Poison-message guard: no reintentar indefinidamente registros que el
      // servidor rechaza (4xx). Quedan en cola marcados con error para revisión.
      if ((rec.attempts || 0) >= MAX_ATTEMPTS) { failed++; continue; }
      try {
        const resolved = await uploadAndResolve(rec.payload, rec.photos);
        await axios({ method: rec.method || "post", url: `${API}${rec.endpoint}`, data: resolved });
        await queueDelete(rec.id);
        synced++;
      } catch (e) {
        if (isNetworkError(e)) break; // se cayó la conexión de nuevo
        rec.attempts = (rec.attempts || 0) + 1;
        rec.error = e?.response?.data?.detail || "Error al sincronizar";
        await (await db()).put("pending", rec);
        failed++;
      }
    }
  } finally { _syncing = false; notify(); }
  return { synced, failed };
}

// ===== Suscripción para la UI =====
const listeners = new Set();
export function onQueueChange(cb) { listeners.add(cb); return () => listeners.delete(cb); }
function notify() { listeners.forEach((cb) => { try { cb(); } catch { /* noop */ } }); }
